import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../services/supabase.ts';
import { formatCurrency, formatDate } from '../utils/formatters.ts';
import LoadingSpinner from '../components/shared/LoadingSpinner.tsx';
import ErrorMessage from '../components/shared/ErrorMessage.tsx';
import '../styles/TenantSettlements.css';

const TenantSettlementsPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenant, setTenant] = useState<any>(null);
  const [settlements, setSettlements] = useState<any[]>([]);

  const fetchTenantSettlements = useCallback(async () => {
    try {
      // Fetch tenant details
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('*, property:properties(*)')
        .eq('id', id)
        .single();

      if (tenantError) throw tenantError;
      setTenant(tenantData);

      // Fetch settlements
      const { data: settlementsData, error: settlementsError } = await supabase
        .from('tenant_settlements')
        .select('*, property:properties(name)')
        .eq('tenant_id', id)
        .order('created_at', { ascending: false }); // Change this line to order by created_at timestamp

      if (settlementsError) throw settlementsError;
      setSettlements(settlementsData || []);

    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load settlements');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTenantSettlements();
  }, [fetchTenantSettlements]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!tenant) return <ErrorMessage message="Tenant not found" />;

  return (
    <div className="tenant-settlements-page">
      <div className="page-header">
        <button className="btn-back" onClick={() => window.history.back()}>
          ← Back
        </button>
        <div className="header-content">
          <h1>{tenant.full_name}'s Settlements</h1>
          <p className="tenant-info">
            {tenant.property?.name} • {tenant.phone} • {tenant.email}
          </p>
        </div>
      </div>

      <div className="settlements-table-container">
        {settlements.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Property</th>
                <th>Total Amount</th>
                <th>Paid Amount</th>
                <th>Outstanding</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {settlements.map((settlement) => (
                <tr key={settlement.id}>
                  <td>{formatDate(settlement.settlement_date)}</td>
                  <td>{settlement.property.name}</td>
                  <td>{formatCurrency(settlement.total_amount)}</td>
                  <td className="amount-paid">{formatCurrency(settlement.paid_amount)}</td>
                  <td className={`amount-pending ${settlement.outstanding_amount === 0 ? 'zero-amount' : ''}`}>
                    {formatCurrency(settlement.outstanding_amount)}
                  </td>
                  <td>
                    <span className={`status-badge ${settlement.status.toLowerCase()}`}>
                      {settlement.status}
                    </span>
                  </td>
                  <td>{settlement.settlement_notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-settlements">
            No settlements found for this tenant
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantSettlementsPage;
